import { Combobox } from "@headlessui/react";
import { CheckIcon, ChevronUpDownIcon } from "@heroicons/react/24/solid";
import clsx from "clsx";
import React, { useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { useGetAdditionalAlignersPatientsQuery } from "~/api";
import { Label } from "~/components/ui/label";
import type { TPatient } from "~/types/patient";

type TPatientOption = { id: number; name: string; steps: number };

export function PatientCombobox({
  selectedPatientId,
  onPatientIdChange,
  readOnly,
}: {
  selectedPatientId?: TPatient["patient_id"];
  onPatientIdChange(newPatientId?: TPatient["patient_id"]): void;
  readOnly?: boolean;
}) {
  const intl = useIntl();
  const buttonRef = useRef<HTMLButtonElement>(null);

  const [query, setQuery] = useState("");

  const patientsQuery = useGetAdditionalAlignersPatientsQuery();
  const patients = patientsQuery.data ?? [];

  const selectedPatient = patients.find((patient) => patient.id == selectedPatientId) ?? null;
  const filteredPatients = filterPatients(patients, query);

  return (
    <div className="form-group">
      <Label
        id="validation-patient"
        htmlFor="patient-input"
        required
        tooltip={<FormattedMessage id="PAT_INFO_NAME_TOOLTIP" />}
      >
        <FormattedMessage id="additional_aligners.patient.label" />
      </Label>

      <div className="tw-relative tw-w-full sm:tw-max-w-xs">
        <Combobox
          disabled={patientsQuery.isLoading || readOnly}
          value={selectedPatient}
          onChange={(patient) => onPatientIdChange(patient?.id)}
          nullable
        >
          {({ open }) => (
            <>
              <div
                className={clsx(
                  "tw-relative tw-w-full tw-overflow-hidden tw-border tw-border-[#c2cad8] tw-bg-white enabled:hover:tw-border-gray-400",
                  "hover: tw-shadow-md tw-transition-all focus-within:tw-ring-1 focus-within:tw-ring-blue-600",
                )}
              >
                <Combobox.Input
                  id="patient-input"
                  autoComplete="off"
                  placeholder={intl.formatMessage({
                    id: patientsQuery.isLoading
                      ? "loading"
                      : "additional_aligners.patient.placeholder",
                  })}
                  displayValue={(patient: TPatientOption | null) => patient?.name ?? ""}
                  className={clsx(
                    "tw-w-full tw-cursor-default tw-border-none tw-py-2 tw-pl-3 tw-pr-10",
                    "tw-text-base tw-text-[#4d6b8a] tw-outline-none sm:tw-text-sm",
                  )}
                  onChange={(event) => setQuery(event.target.value)}
                  onClick={() => {
                    if (!open) {
                      buttonRef.current?.click();
                    }
                  }}
                  onFocus={() => {
                    if (!open) {
                      buttonRef.current?.click();
                    }
                  }}
                />

                <Combobox.Button
                  ref={buttonRef}
                  className={clsx(
                    "tw-absolute tw-inset-y-0 tw-right-0 tw-flex tw-items-center tw-border-none",
                    "tw-cursor-default tw-bg-[#efefef] tw-pr-2 tw-transition-all enabled:hover:tw-bg-[#e0e0e0]",
                  )}
                >
                  <ChevronUpDownIcon
                    className="tw-h-5 tw-w-5 tw-text-gray-400"
                    aria-hidden="true"
                  />
                </Combobox.Button>
              </div>

              <Combobox.Options
                className={clsx(
                  "tw-absolute tw-z-10 tw-mt-1 tw-max-h-60 tw-w-full tw-overflow-auto tw-py-1 tw-pl-0",
                  "tw-cursor-default tw-border tw-border-[#c2cad8] tw-bg-white tw-shadow-lg",
                )}
              >
                {filteredPatients == undefined ? (
                  <div className="tw-w-full tw-px-4 tw-py-2">
                    <FormattedMessage id="loading" />
                  </div>
                ) : patients.length == 0 ? (
                  <div className="tw-w-full tw-px-4 tw-py-2">
                    <FormattedMessage id="additional_aligners.patient.none" />
                  </div>
                ) : filteredPatients.length == 0 && query != "" ? (
                  <div className="tw-w-full tw-px-4 tw-py-2">
                    <FormattedMessage id="additional_aligners.patient.not_found" />
                  </div>
                ) : (
                  filteredPatients.map((patient) => (
                    <Combobox.Option
                      key={patient.id}
                      value={patient}
                      className={({ active }) =>
                        clsx("tw-relative tw-py-2 tw-pl-10 tw-pr-4", {
                          "tw-bg-brand-green tw-text-white": active,
                        })
                      }
                    >
                      {({ selected, active }) => (
                        <>
                          <span
                            className={clsx(
                              "tw-block tw-truncate",
                              selected ? "tw-font-semibold" : "tw-font-normal",
                            )}
                          >
                            {`${patient.name} (${patient.id})`}
                          </span>

                          {selected ? (
                            <span className="tw-absolute tw-inset-y-0 tw-left-0 tw-flex tw-items-center tw-pl-3">
                              <CheckIcon
                                className={clsx(
                                  "tw-h-5 tw-w-5",
                                  active ? "tw-text-white" : "tw-text-brand-green",
                                )}
                                aria-hidden="true"
                              />
                            </span>
                          ) : null}
                        </>
                      )}
                    </Combobox.Option>
                  ))
                )}
              </Combobox.Options>
            </>
          )}
        </Combobox>
      </div>
    </div>
  );
}

function filterPatients(patients: TPatientOption[], query: string): TPatientOption[] {
  const lowerQuery = query.toLowerCase().trim();

  if (patients.length == 0 || lowerQuery == "") {
    return patients;
  }

  const filteredPatients = patients.filter(
    (patient) =>
      patient.name.toLowerCase().includes(lowerQuery) ||
      patient.id.toString().toLowerCase().includes(lowerQuery),
  );

  return filteredPatients;
}
